import { Component } from '@angular/core';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  faCircleArrowLeft,
  faPaperPlane,
  faPenToSquare,
  faTrash,
  faCirclePlus,
  faCircleArrowRight,
  faSearch,
  faHouse,
  faBuilding,
  faHouseChimney,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { MessageResponse } from '../_models/responses';
import { environment } from '../../environments/environment';
import { Complaint, ComplaintService } from '../_services';
import { PropertyService } from '../_services/property.service';
import { AuthenticationService } from '../_services/authentication.service';
import { ComplaintStatus, ComplaintStatusLabels, getComplaintStatusLabel } from '../_models/complaint-status';
import { Property } from '../_models/property';

@Component({
  selector: 'app-manage-complaints',
  standalone: false,
  templateUrl: './manage-complaints.component.html',
  styleUrl: './manage-complaints.component.css'
})
export class ManageComplaintsComponent {
  action: string = 'list';
  searchProperty = '';

  faCircleArrowLeft = faCircleArrowLeft;
  faPaperPlane = faPaperPlane;
  faPenToSquare = faPenToSquare;
  faTrash = faTrash;
  faCirclePlus = faCirclePlus;
  faCircleArrowRight = faCircleArrowRight;
  faSearch = faSearch;
  faHouse = faHouse;
  faBuilding = faBuilding;
  faHouseChimney = faHouseChimney;

  loading = false;
  loadingProperties = false;
  fetching = false;
  submitComplaintData = false;

  searchComplaint = '';

  complaints: Complaint[] = [];
  properties: Property[] = [];
  property: any;
  selectedComplaint: Complaint | null = null;

  ComplaintStatus = ComplaintStatus;
  ComplaintStatusLabels = ComplaintStatusLabels;
  getComplaintStatusLabel = getComplaintStatusLabel;

  isAdmin = false;
  isSuperAdmin = false;

  addComplaintForm!: FormGroup;
  editComplaintForm!: FormGroup;
  viewComplaintForm!: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _location: Location,
    private formBuilder: FormBuilder,
    private complaintService: ComplaintService,
    private propertyService: PropertyService,
    private authService: AuthenticationService,
    private toastr: ToastrService
  ) {
    this.checkUserRole();
  }

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      this.action = params['action'] || 'list';
      this.initializeForms();
      this.loadPropertyContextForAction();
    });
  }

  get fca() {
    return this.addComplaintForm.controls;
  }

  get fce() {
    return this.editComplaintForm.controls;
  }

  goBack(): void {
    this._location.back();
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  private checkUserRole(): void {
    const currentUser = this.authService.currentUserValue;
    if (currentUser?.role) {
      this.isAdmin = currentUser.role.includes('ROLE_ADMIN');
      this.isSuperAdmin = currentUser.role.includes('ROLE_SUPER');
    }
  }

  canEditStatus(): boolean {
    return this.isAdmin || this.isSuperAdmin;
  }

  onCreateComplaint(): void {
    this.router.navigate(['/manage-complaints/create']);
  }

  onCancel(): void {
    this.router.navigate(['/manage-complaints/list']);
  }

  onSelectedComplaint(complaint: Complaint): void {
    this.selectedComplaint = complaint;
    localStorage.setItem('complaint', JSON.stringify(complaint));

    this.viewComplaintForm.patchValue({
      id: complaint.id,
      name_complainant: complaint.name_complainant,
      address_complainant: complaint.address_complainant,
      complainant_email: complaint.complainant_email,
      complainant_type: complaint.complainant_type,
      complainant_section_strata_lotno: complaint.complainant_section_strata_lotno,
      name_complainee: complaint.name_complainee,
      address_complainee: complaint.address_complainee,
      complainee_email: complaint.complainee_email,
      complainee_section_strata_lotno: complaint.complainee_section_strata_lotno,
      grounds_complaint: complaint.grounds_complaint,
      status: complaint.status,
      date_submission: this.formatDateInput(complaint.date_submission),
      propertyId: complaint.propertyId,
      propertyName: this.property?.name || '',
    });

    this.router.navigate(['/manage-complaints/view']);
  }

  onEditComplaint(): void {
    if (!this.selectedComplaint) {
      this.toastr.error('No complaint selected for editing.', 'Error');
      return;
    }

    this.editComplaintForm.patchValue({
      id: this.selectedComplaint.id,
      name_complainant: this.selectedComplaint.name_complainant,
      address_complainant: this.selectedComplaint.address_complainant,
      complainant_email: this.selectedComplaint.complainant_email,
      complainant_type: this.selectedComplaint.complainant_type,
      complainant_section_strata_lotno: this.selectedComplaint.complainant_section_strata_lotno,
      name_complainee: this.selectedComplaint.name_complainee,
      address_complainee: this.selectedComplaint.address_complainee,
      complainee_email: this.selectedComplaint.complainee_email,
      complainee_section_strata_lotno: this.selectedComplaint.complainee_section_strata_lotno,
      grounds_complaint: this.selectedComplaint.grounds_complaint,
      status: this.selectedComplaint.status,
      date_submission: this.formatDateInput(this.selectedComplaint.date_submission),
      propertyId: this.selectedComplaint.propertyId,
      propertyName: this.property?.name || '',
    });

    if (!this.canEditStatus()) {
      this.editComplaintForm.get('status')?.disable();
    }

    this.router.navigate(['/manage-complaints/edit']);
  }

  createComplaint(): void {
    this.submitComplaintData = true;
    this.fetching = true;

    if (!this.addComplaintForm.valid) {
      this.fetching = false;
      return;
    }

    const formdata = this.addComplaintForm.value;
    const payload = this.buildPayload(formdata);

    this.complaintService.createComplaint(payload).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        this.submitComplaintData = false;
        this.loadComplaintsByProperty();
        this.router.navigate(['/manage-complaints/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to create complaint.', 'Error');
        if (!environment.production) {
          console.log('Complaint creation failed: ', error);
        }
      },
    });
  }

  updateComplaint(): void {
    this.submitComplaintData = true;
    this.fetching = true;

    if (!this.editComplaintForm.valid) {
      this.fetching = false;
      return;
    }

    const formdata = this.editComplaintForm.value;
    const complaintId = formdata.id;
    const payload = this.buildPayload(formdata);

    this.complaintService.updateComplaint(complaintId, payload).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        this.submitComplaintData = false;
        localStorage.setItem('complaint', JSON.stringify({ id: complaintId, ...payload }));
        this.loadComplaintsByProperty();
        this.router.navigate(['/manage-complaints/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to update complaint.', 'Error');
        if (!environment.production) {
          console.log('Complaint update failed: ', error);
        }
      },
    });
  }

  onDeleteComplaint(): void {
    const complaintId = this.selectedComplaint?.id || this.editComplaintForm?.value?.id || this.viewComplaintForm?.value?.id;
    if (!complaintId) {
      this.toastr.error('No complaint selected for deletion.', 'Error');
      return;
    }

    this.fetching = true;
    this.complaintService.deleteComplaint(complaintId).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        localStorage.removeItem('complaint');
        this.selectedComplaint = null;
        this.loadComplaintsByProperty();
        this.router.navigate(['/manage-complaints/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to delete complaint.', 'Error');
        if (!environment.production) {
          console.log('Complaint delete failed: ', error);
        }
      },
    });
  }

  private initializeForms(): void {
    this.addComplaintForm = this.formBuilder.group({
      name_complainant: ['', Validators.required],
      address_complainant: ['', Validators.required],
      complainant_email: ['', [Validators.required, Validators.email]],
      complainant_type: ['', Validators.required],
      complainant_section_strata_lotno: ['', Validators.required],
      name_complainee: ['', Validators.required],
      address_complainee: ['', Validators.required],
      complainee_email: ['', [Validators.required, Validators.email]],
      complainee_section_strata_lotno: ['', Validators.required],
      grounds_complaint: ['', Validators.required],
      status: [ComplaintStatus.Capture, Validators.required],
      date_submission: ['', Validators.required],
      propertyId: ['', Validators.required],
      propertyName: [''],
    });

    this.editComplaintForm = this.formBuilder.group({
      id: ['', Validators.required],
      name_complainant: ['', Validators.required],
      address_complainant: ['', Validators.required],
      complainant_email: ['', [Validators.required, Validators.email]],
      complainant_type: ['', Validators.required],
      complainant_section_strata_lotno: ['', Validators.required],
      name_complainee: ['', Validators.required],
      address_complainee: ['', Validators.required],
      complainee_email: ['', [Validators.required, Validators.email]],
      complainee_section_strata_lotno: ['', Validators.required],
      grounds_complaint: ['', Validators.required],
      status: [ComplaintStatus.Capture, Validators.required],
      date_submission: ['', Validators.required],
      propertyId: ['', Validators.required],
      propertyName: [''],
    });

    this.viewComplaintForm = this.formBuilder.group({
      id: [{ value: '', disabled: true }, Validators.required],
      name_complainant: [{ value: '', disabled: true }, Validators.required],
      address_complainant: [{ value: '', disabled: true }, Validators.required],
      complainant_email: [{ value: '', disabled: true }, Validators.required],
      complainant_type: [{ value: '', disabled: true }, Validators.required],
      complainant_section_strata_lotno: [{ value: '', disabled: true }, Validators.required],
      name_complainee: [{ value: '', disabled: true }, Validators.required],
      address_complainee: [{ value: '', disabled: true }, Validators.required],
      complainee_email: [{ value: '', disabled: true }, Validators.required],
      complainee_section_strata_lotno: [{ value: '', disabled: true }, Validators.required],
      grounds_complaint: [{ value: '', disabled: true }, Validators.required],
      status: [{ value: '', disabled: true }, Validators.required],
      date_submission: [{ value: '', disabled: true }, Validators.required],
      propertyId: [{ value: '', disabled: true }, Validators.required],
      propertyName: [{ value: '', disabled: true }],
    });
  }

  private loadPropertyContextForAction(): void {
    this.loadingProperties = true;
    this.propertyService.getMyPropertyAssociations().subscribe({
      next: (associations: any[]) => {
        this.properties = associations.map(assoc => ({
          id: assoc.propertyId,
          name: assoc.propertyDetails.name,
          property_type: assoc.propertyDetails.property_type,
          address: assoc.propertyDetails.address,
          obligationCategoryId: assoc.propertyDetails.obligationCategoryId ?? 0,
          premises: []
        })) || [];

        if (this.properties.length === 1) {
          this.setSelectedProperty(this.properties[0]);
        } else {
          const propertystr = localStorage.getItem('property');
          const currentProperty = propertystr ? JSON.parse(propertystr) : null;
          if (currentProperty?.id) {
            const matched = this.properties.find((p) => p.id === currentProperty.id);
            if (matched) {
              this.setSelectedProperty(matched);
            }
          }
        }

        if (this.properties.length > 1 && this.action === 'list') {
          this.action = 'index';
        }

        this.loadingProperties = false;
        this.loadComplaintStateForAction();
      },
      error: () => {
        this.loadingProperties = false;
        this.toastr.error('Failed to load properties.', 'Error');
      },
    });
  }

  onSelectedProperty(property: Property): void {
    this.setSelectedProperty(property);
    this.action = 'list';
    this.loadComplaintStateForAction();
  }

  onChangeProperty(): void {
    this.action = 'index';
  }

  private setSelectedProperty(property: Property): void {
    this.property = property;
    this.propertyService.setProperty(property);
    this.addComplaintForm.patchValue({
      propertyId: this.property.id,
      propertyName: this.property.name,
    });
  }

  private loadComplaintStateForAction(): void {
    if (this.action === 'index') {
      return;
    }

    if (!this.property?.id) {
      return;
    }

    if (this.action === 'list') {
      this.loadComplaintsByProperty();
      return;
    }

    if (this.action === 'create') {
      this.addComplaintForm.patchValue({
        name_complainant: '',
        address_complainant: '',
        complainant_email: '',
        complainant_type: '',
        complainant_section_strata_lotno: '',
        name_complainee: '',
        address_complainee: '',
        complainee_email: '',
        complainee_section_strata_lotno: '',
        grounds_complaint: '',
        status: ComplaintStatus.Capture,
        date_submission: '',
        propertyId: this.property?.id || '',
        propertyName: this.property?.name || '',
      });
      return;
    }

    const complaintstr = localStorage.getItem('complaint');
    this.selectedComplaint = complaintstr ? JSON.parse(complaintstr) : null;

    if (!this.selectedComplaint?.id) {
      this.toastr.warning('Select a complaint from the list first.', 'Info');
      this.router.navigate(['/manage-complaints/list']);
      return;
    }

    this.complaintService.getComplaint(this.selectedComplaint.id).subscribe({
      next: (complaint: Complaint) => {
        this.selectedComplaint = complaint;
        localStorage.setItem('complaint', JSON.stringify(complaint));

        if (this.action === 'view') {
          this.viewComplaintForm.patchValue({
            id: complaint.id,
            name_complainant: complaint.name_complainant,
            address_complainant: complaint.address_complainant,
            complainant_email: complaint.complainant_email,
            complainant_type: complaint.complainant_type,
            complainant_section_strata_lotno: complaint.complainant_section_strata_lotno,
            name_complainee: complaint.name_complainee,
            address_complainee: complaint.address_complainee,
            complainee_email: complaint.complainee_email,
            complainee_section_strata_lotno: complaint.complainee_section_strata_lotno,
            grounds_complaint: complaint.grounds_complaint,
            status: complaint.status,
            date_submission: this.formatDateInput(complaint.date_submission),
            propertyId: complaint.propertyId,
            propertyName: this.property?.name || '',
          });
        }

        if (this.action === 'edit') {
          this.editComplaintForm.patchValue({
            id: complaint.id,
            name_complainant: complaint.name_complainant,
            address_complainant: complaint.address_complainant,
            complainant_email: complaint.complainant_email,
            complainant_type: complaint.complainant_type,
            complainant_section_strata_lotno: complaint.complainant_section_strata_lotno,
            name_complainee: complaint.name_complainee,
            address_complainee: complaint.address_complainee,
            complainee_email: complaint.complainee_email,
            complainee_section_strata_lotno: complaint.complainee_section_strata_lotno,
            grounds_complaint: complaint.grounds_complaint,
            status: complaint.status,
            date_submission: this.formatDateInput(complaint.date_submission),
            propertyId: complaint.propertyId,
            propertyName: this.property?.name || '',
          });
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastr.error('Failed to load selected complaint.', 'Error');
        if (!environment.production) {
          console.log('Complaint fetch failed: ', error);
        }
        this.router.navigate(['/manage-complaints/list']);
      },
    });
  }

  private loadComplaintsByProperty(): void {
    if (!this.property?.id) {
      this.complaints = [];
      return;
    }

    this.loading = true;
    this.complaintService.getComplaintsByPropertyId(this.property.id).subscribe({
      next: (complaints: Complaint[]) => {
        this.complaints = complaints || [];
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.toastr.error('Failed to load complaints.', 'Error');
        if (!environment.production) {
          console.log('Complaint list fetch failed: ', error);
        }
      },
    });
  }

  private buildPayload(formdata: any) {
    return {
      name_complainant: formdata.name_complainant,
      address_complainant: formdata.address_complainant,
      complainant_email: formdata.complainant_email,
      complainant_type: formdata.complainant_type,
      complainant_section_strata_lotno: formdata.complainant_section_strata_lotno,
      name_complainee: formdata.name_complainee,
      address_complainee: formdata.address_complainee,
      complainee_email: formdata.complainee_email,
      complainee_section_strata_lotno: formdata.complainee_section_strata_lotno,
      grounds_complaint: formdata.grounds_complaint,
      status: formdata.status,
      date_submission: formdata.date_submission,
      propertyId: Number(formdata.propertyId),
    };
  }

  private formatDateInput(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
